import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { fetchIntegrations, saveIntegration } from "redux/slice/integrations";
import { getUser } from "redux/selector";
import { copyFor, isWhiteLabel } from "../../follow-up-boss-copy";
import { Integration } from "model/integration";
import * as api from "api/api";
import { AutoCreateToggle } from "./auto-create-toggle";
import { AgentSelectionTable } from "./agent-selection-table";
import { AgentSelectionState, buildSaveRequest, initialSelectionState, isSelectionDirty, isSelectionLocked, validateSelection } from "./agent-selection";
import { FollowUpBossAgentSelection } from "model/follow-up-boss-agent-selection";
import { Tag } from "component/shared/tag";
import { FormField } from "component/shared/form-fields";

const mapStateToProps = (state: RootState) => ({
    user: getUser(state),
});
const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    fetchIntegrations,
    saveIntegration,
}, dispatch);

type AgentSync = "off" | "sync_data" | "sync_data_and_create";
type MergeStrategy = "fill_in_blanks" | "fub_wins" | "ihf_wins";

// Sticky action bar: footer buttons stay visible while the modal body scrolls (MAX-3998).
// 16px margin + 16px padding gives the design's 32px gap from the end of content.
const stickyFooterStyle: React.CSSProperties = {
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fff",
    marginTop: 16,
    paddingTop: 16,
    paddingBottom: 16,
    zIndex: 1,
};

interface Props extends
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps>
{
    integration: Integration;
    agentSync: AgentSync;
    mergeStrategy: MergeStrategy;
    onStatusChange: (patch: {
        connected?: boolean;
        webhooksMissing?: number;
        webhookLastHealthCheckDate?: number | null;
        agentsMatched?: number | null;
        ihfAgentsNotInFub?: number | null;
        fubUsersNotInIhf?: number | null;
        leadsMatched?: number | null;
        leadsUnmatched?: number | null;
        mergeStrategy?: MergeStrategy;
        agentSync?: AgentSync;
        apiKeyValid?: boolean | null;
    }) => void;
    onClose?: () => void;
    // MAX-4236: the modal keeps the last loaded selection so a tab switch (which remounts this
    // tab) does not cost another live FUB users call. Retry and save still refresh it.
    cachedSelection?: FollowUpBossAgentSelection | null;
    onSelectionLoaded?: (selection: FollowUpBossAgentSelection) => void;
}

interface State {
    agentSyncCreate: boolean;
    mergeStrategy: MergeStrategy;
    apiKey: string;
    saving: boolean;
    saveMessage: string | null;
    saveError: string | null;
    disconnecting: boolean;
    confirmDisconnectOpen: boolean;
    // MAX-4236 agent selection (selection mode only)
    selection: FollowUpBossAgentSelection | null;
    selectionState: AgentSelectionState | null;
    selectionLoading: boolean;
    selectionError: string | null;
}

class Component extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            agentSyncCreate: props.agentSync === "sync_data_and_create",
            mergeStrategy: props.mergeStrategy,
            apiKey: (props.integration.data?.externalKey as string) ?? "",
            saving: false,
            saveMessage: null,
            saveError: null,
            disconnecting: false,
            confirmDisconnectOpen: false,
            selection: null,
            selectionState: null,
            selectionLoading: false,
            selectionError: null,
        };
    }

    public componentDidMount() {
        if (!this.isSelectionMode()) {
            return;
        }
        const cached = this.props.cachedSelection;
        if (cached) {
            this.setState({ selection: cached, selectionState: initialSelectionState(cached) });
        } else {
            this.loadAgentSelection();
        }
    }

    private initialApiKey() {
        return (this.props.integration.data?.externalKey as string) ?? "";
    }

    private handleCancel() {
        const { selection } = this.state;
        this.setState({
            agentSyncCreate: this.props.agentSync === "sync_data_and_create",
            mergeStrategy: this.props.mergeStrategy,
            apiKey: this.initialApiKey(),
            saveMessage: null,
            saveError: null,
            selectionState: selection ? initialSelectionState(selection) : null,
            selectionError: null,
        });
    }

    // MAX-4236: the FollowUpBossAgentSelection billing feature (see connection.tsx)
    private isSelectionMode(): boolean {
        return !!this.props.user.permissions.followUpBossAgentSelection;
    }

    private async loadAgentSelection() {
        const { integration } = this.props;
        this.setState({ selectionLoading: true, selectionError: null });
        try {
            const selection = await api.getFollowUpBossAgentSelection(integration.id);
            this.setState({ selection, selectionState: initialSelectionState(selection), selectionLoading: false });
            this.props.onSelectionLoaded?.(selection);
        } catch(e) {
            const serverMessage = e instanceof Error && e.message ? e.message : null;
            this.setState({ selection: null, selectionState: null, selectionLoading: false, selectionError: serverMessage ?? "Could not load Follow Up Boss users." });
        }
    }

    // Auto-create requires the FollowUpBossAgentSync billing feature; without it the
    // toggle is hidden and the setting can only be saved as sync_data
    private isAgentSyncPermitted(): boolean {
        return !!this.props.user.permissions.followUpBossAgentSync;
    }

    // MAX-4028: fixed-seat plans can't add agent seats — auto-create is locked OFF.
    // Evaluated live from permissions so a plan upgrade unlocks it on next load.
    private isFixedSeatPlan(): boolean {
        return this.props.user.permissions.agentSeatCap != null;
    }

    private async handleSaveChanges() {
        const { integration, saveIntegration, fetchIntegrations, onStatusChange } = this.props;
        const { agentSyncCreate, mergeStrategy, apiKey } = this.state;
        const agentSync: AgentSync = this.isAgentSyncPermitted() && !this.isFixedSeatPlan() && !this.isSelectionMode() && agentSyncCreate ? "sync_data_and_create" : "sync_data";
        this.setState({ saving: true, saveMessage: null, saveError: null, selectionError: null });

        // Validate + store a rotated API key first — the backend rejects keys that
        // belong to a different Follow Up Boss account
        if (apiKey.trim() && apiKey.trim() !== this.initialApiKey()) {
            try {
                await api.connectFollowUpBoss(integration.id, apiKey.trim());
                onStatusChange({ apiKeyValid: true });
                fetchIntegrations();
            } catch(e) {
                // Prefer the backend's user-presentable message (wrong account, webhook
                // limit) over the generic assumption that the key itself is bad (MAX-4012)
                const serverMessage = e instanceof Error && e.message ? e.message : null;
                this.setState({
                    saving: false,
                    saveError: serverMessage ?? "API key update failed. Verify the key is correct and belongs to the same Follow Up Boss account.",
                });
                return;
            }
        }

        // MAX-4236: the agent selection saves first; a rejection (cap, conflict) stops the save
        // so the user sees the message next to the table instead of a half-applied change.
        // MAX-4579: only an unconfirmed selection (an account connected before its package gained
        // agent selection) is ever PUT; the first save confirms and locks it, and a confirmed one
        // is rendered read-only and never sent again.
        const { selection, selectionState } = this.state;
        if (this.isSelectionMode() && selection && selectionState && isSelectionDirty(selectionState, selection)) {
            const clientError = validateSelection(selectionState, selection.seatCap, isWhiteLabel(this.props.user));
            if (clientError) {
                this.setState({ saving: false, selectionError: clientError });
                return;
            }
            try {
                const saved = await api.saveFollowUpBossAgentSelection(integration.id, buildSaveRequest(selectionState));
                this.setState({ selection: saved, selectionState: initialSelectionState(saved) });
                this.props.onSelectionLoaded?.(saved);
            } catch(e) {
                const serverMessage = e instanceof Error && e.message ? e.message : null;
                this.setState({ saving: false, selectionError: serverMessage ?? "Saving the agent selection failed. Please try again." });
                return;
            }
        }

        try {
            await saveIntegration({ integration: { ...integration, data: {
                followUpBossAgentSyncMode: agentSync,
                followUpBossMergeStrategy: mergeStrategy,
            }}});
            onStatusChange({ agentSync, mergeStrategy });
            this.setState({ saving: false, saveMessage: "Changes saved" });
            setTimeout(() => this.setState({ saveMessage: null }), 3000);
        } catch(_e) {
            this.setState({ saving: false, saveError: "Failed to save. Please try again." });
        }
    }

    private async handleDisconnect() {
        const { integration, fetchIntegrations, onStatusChange, onClose } = this.props;
        this.setState({ disconnecting: true });
        try {
            await api.disconnectFollowUpBoss(integration.id);
            this.setState({ disconnecting: false, confirmDisconnectOpen: false });
            onStatusChange({
                connected: false,
                agentsMatched: null,
                ihfAgentsNotInFub: null,
                fubUsersNotInIhf: null,
                leadsMatched: null,
                leadsUnmatched: null,
                webhooksMissing: 0,
                webhookLastHealthCheckDate: null,
            });
            fetchIntegrations();
            if (onClose) onClose();
        } catch(_e) {
            this.setState({ disconnecting: false, confirmDisconnectOpen: false, saveError: "Disconnect failed. Please try again." });
        }
    }

    public render() {
        const {
            agentSyncCreate, mergeStrategy, apiKey,
            saving, saveMessage, saveError,
            disconnecting, confirmDisconnectOpen,
            selection, selectionState, selectionLoading, selectionError,
        } = this.state;
        const selectionMode = this.isSelectionMode();
        const copy = copyFor(this.props.user);

        return (
            <Mui.Box>
                {/* MAX-4236: Agent Selection — which FUB users import, mapped to IHF agents */}
                {selectionMode && (
                    <>
                        <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 16 }}>
                            Agent Selection
                        </Mui.Typography>
                        <AgentSelectionTable
                            selection={selection}
                            state={selectionState}
                            loading={selectionLoading}
                            error={selectionError}
                            disabled={saving}
                            readOnly={isSelectionLocked(selection)}
                            onChange={(next) => this.setState({ selectionState: next, selectionError: null })}
                            onRetry={() => this.loadAgentSelection()}
                            showSyncHint
                            showEmptyWarning
                            whiteLabel={isWhiteLabel(this.props.user)}
                        />
                        <Mui.Divider style={{ margin: "24px 0" }} />
                    </>
                )}

                {/* Agent Matching — MAX-4579: not shown in selection mode */}
                {this.isAgentSyncPermitted() && !selectionMode && (
                    <>
                        <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 24 }}>
                            Agent Matching
                        </Mui.Typography>
                        <AutoCreateToggle
                            checked={!this.isFixedSeatPlan() && agentSyncCreate}
                            onChange={(checked) => this.setState({ agentSyncCreate: checked })}
                            lockedOff={this.isFixedSeatPlan()}
                            whiteLabel={isWhiteLabel(this.props.user)}
                        />
                        <Mui.Divider style={{ margin: "24px 0" }} />
                    </>
                )}

                {/* Data Conflicts */}
                <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 8 }}>
                    Data Conflicts
                </Mui.Typography>
                <Mui.Typography variant="body2" style={{ color: "#1C1B1F", marginBottom: 8 }}>
                    These options only apply when all records sync at once (initial sync or Sync Now). Individual record updates sync automatically based on the most recent update, regardless of this setting.
                </Mui.Typography>
                <Mui.Typography variant="body2" style={{ color: "#1C1B1F" }}>
                    This setting also has no effect on new lead creation.
                </Mui.Typography>
                <Mui.Box marginTop={3}>
                            <Mui.RadioGroup
                                value={mergeStrategy}
                                onChange={(e) => this.setState({ mergeStrategy: e.target.value as MergeStrategy })}
                            >
                                {([
                                    {
                                        value: "fill_in_blanks",
                                        label: "Fill missing fields only",
                                        recommended: true,
                                        description: "If a field is empty in one system but has a value in the other, the empty field is filled in. Existing data in either system is never overwritten.",
                                    },
                                    {
                                        value: "ihf_wins",
                                        label: copy.conflictIhfLabel,
                                        description: copy.conflictIhfSettings,
                                    },
                                    {
                                        value: "fub_wins",
                                        label: "Prioritize Follow Up Boss data",
                                        description: copy.conflictFubSettings,
                                    },
                                ] as { value: string; label: string; recommended?: boolean; description: string }[]).map((opt, idx, arr) => (
                                    <Mui.FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        control={<Mui.Radio color="secondary" size="small" style={{ alignSelf: "flex-start", paddingTop: 4 }} />}
                                        style={{ alignItems: "flex-start", marginBottom: idx === arr.length - 1 ? 0 : 24 }}
                                        label={
                                            <Mui.Box style={{ paddingTop: 2 }}>
                                                <Mui.Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                                                    <Mui.Typography variant="body2" style={{ fontWeight: 600, color: "#1C1B1F" }}>{opt.label}</Mui.Typography>
                                                    {opt.recommended && (
                                                        <Tag label="Recommended" variant="success" size="small" />
                                                    )}
                                                </Mui.Box>
                                                <Mui.Typography variant="body2" style={{ marginTop: 8, color: "#1C1B1F" }}>{opt.description}</Mui.Typography>
                                            </Mui.Box>
                                        }
                                    />
                                ))}
                            </Mui.RadioGroup>
                </Mui.Box>

                <Mui.Divider style={{ margin: "24px 0" }} />

                {/* API Connection */}
                <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 8 }}>
                    API Connection
                </Mui.Typography>
                <Mui.Typography variant="body2" style={{ color: "#1C1B1F" }}>
                    Your API key is used to authenticate your Follow Up Boss connection. You may update your key if it has been regenerated, but it must belong to the same Follow Up Boss account.
                </Mui.Typography>
                <Mui.Box marginTop={3}>
                    <FormField
                        label="API Key"
                        value={apiKey}
                        onChange={(e) => this.setState({ apiKey: e.target.value })}
                        placeholder="fka_000000000000000000000000"
                        disabled={saving}
                    />
                </Mui.Box>

                <Mui.Divider style={{ margin: "24px 0" }} />

                {/* Disconnect Integration */}
                <Mui.Typography variant="subtitle1" style={{ fontWeight: 700, fontSize: 16, color: "#1C1B1F", marginBottom: 8 }}>
                    Disconnect Integration
                </Mui.Typography>
                <Mui.Typography variant="body2" style={{ color: "#1C1B1F" }}>
                    By disconnecting, lead sync and event streaming will stop immediately. If you need to connect to a different Follow Up Boss account, you can set up a new integration after disconnecting.
                </Mui.Typography>
                <Mui.Link
                    component="button"
                    variant="body2"
                    onClick={() => this.setState({ confirmDisconnectOpen: true })}
                    style={{ color: "#e53935", fontWeight: 600, marginTop: 24, display: "inline-block" }}
                >
                    Disconnect Integration
                </Mui.Link>

                {/* Footer */}
                <Mui.Box display="flex" alignItems="center" justifyContent="flex-end" style={{ ...stickyFooterStyle, gap: 12 }}>
                    {saveMessage && (
                        <Mui.Typography variant="body2" style={{ color: "#2e7d32" }}>{saveMessage}</Mui.Typography>
                    )}
                    {saveError && (
                        <Mui.Typography variant="body2" color="error">{saveError}</Mui.Typography>
                    )}
                    <Mui.Button
                        variant="outlined"
                        color="primary"
                        style={{ padding: "7px 16px" }}
                        onClick={() => this.handleCancel()}
                        disabled={saving}
                    >
                        Cancel
                    </Mui.Button>
                    <Mui.Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.handleSaveChanges()}
                        disabled={saving}
                    >
                        {saving ? <Mui.CircularProgress size={18} /> : "Save Changes"}
                    </Mui.Button>
                </Mui.Box>

                {/* Disconnect confirm — design-system confirm modal (MAX-4001, same pattern
                    as Figma 1448-34070): 24px bold title with warning icon and close X,
                    inset divider, 18px body, 32px gap to the actions */}
                <Mui.Dialog
                    open={confirmDisconnectOpen}
                    onClose={() => this.setState({ confirmDisconnectOpen: false })}
                    maxWidth="xs"
                    fullWidth
                >
                    <Mui.DialogTitle style={{ padding: "24px 24px 16px" }}>
                        <Mui.Box display="flex" alignItems="center" justifyContent="space-between">
                            <Mui.Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Icons.AlertTriangle size={24} style={{ color: "#f57f17" }} />
                                <Mui.Typography style={{ fontSize: 24, fontWeight: 700, color: "#1C1B1F" }}>Disconnect Follow Up Boss?</Mui.Typography>
                            </Mui.Box>
                            <Mui.IconButton size="small" onClick={() => this.setState({ confirmDisconnectOpen: false })}>
                                <Icons.X size={20} />
                            </Mui.IconButton>
                        </Mui.Box>
                    </Mui.DialogTitle>
                    <Mui.Divider style={{ margin: "0 24px" }} />
                    <Mui.DialogContent style={{ padding: "16px 24px 0" }}>
                        <Mui.Typography style={{ fontSize: 18, lineHeight: "28px", color: "#1C1B1F" }}>
                            {copy.disconnectWarning}
                        </Mui.Typography>
                    </Mui.DialogContent>
                    <Mui.DialogActions style={{ padding: "32px 24px 24px" }}>
                        <Mui.Button
                            variant="outlined"
                            color="primary"
                            style={{ padding: "7px 16px" }}
                            onClick={() => this.setState({ confirmDisconnectOpen: false })}
                            disabled={disconnecting}
                        >
                            Cancel
                        </Mui.Button>
                        <Mui.Button
                            variant="contained"
                            style={{ backgroundColor: "#e53935", color: "#fff" }}
                            onClick={() => this.handleDisconnect()}
                            disabled={disconnecting}
                        >
                            {disconnecting ? <Mui.CircularProgress size={18} style={{ color: "#fff" }} /> : "Disconnect Integration"}
                        </Mui.Button>
                    </Mui.DialogActions>
                </Mui.Dialog>
            </Mui.Box>
        );
    }

}

export const SettingsTab = connect(mapStateToProps, mapDispatchToProps)(Component);
